// src/pwa.ts
// ============================================================================
// Capa PWA de Let's Move: registro del service worker + suscripción a PUSH.
// El SW (public/sw.js) es mínimo y NO cachea (la frescura la maneja
// autoUpdate.ts). Aquí solo lo registramos y exponemos helpers de push.
// ============================================================================
import { api } from './services/apiClient';

const SW_URL = '/sw.js';

let regPromise: Promise<ServiceWorkerRegistration | null> | null = null;

/** Registra el SW una sola vez. Devuelve el registration (o null si no soportado). */
export function registerSW(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null);
  if (regPromise) return regPromise;
  regPromise = navigator.serviceWorker.register(SW_URL).catch((e) => {
    console.warn('[pwa] SW register falló:', e);
    return null;
  });
  return regPromise;
}

/** ¿La app corre instalada (standalone)? */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Pide permiso (si hace falta), se suscribe a push y registra la suscripción
 * en el backend. Devuelve true si quedó suscrito.
 */
export async function enablePush(): Promise<boolean> {
  if (!pushSupported()) return false;
  const reg = await registerSW();
  if (!reg) return false;

  if (Notification.permission === 'denied') return false;
  if (Notification.permission === 'default') {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return false;
  }

  // Llave pública VAPID del servidor.
  let key: string;
  try {
    const r = await api.get<{ publicKey: string }>('/push/vapid');
    key = r.publicKey;
    if (!key) return false;
  } catch { return false; }

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // `as BufferSource`: con lib.dom nuevo, Uint8Array<ArrayBufferLike> no encaja en
      // ArrayBufferView<ArrayBuffer> aunque en tiempo de ejecución sea exactamente lo que espera.
      applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
    });
  }

  try {
    await api.post('/push/subscribe', sub.toJSON());
    return true;
  } catch {
    return false;
  }
}

export async function pushSubscribed(): Promise<boolean> {
  if (!pushSupported()) return false;
  const reg = await registerSW();
  if (!reg) return false;
  return !!(await reg.pushManager.getSubscription());
}
